import { useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Spinner } from "@heroui/react";
import {
  useCampaignDetailsQuery,
  useCampaignMetricsQuery,
  getCampaignById,
  type CampaignDailyMetric,
} from "@/entities/campaign";
import type { PeriodFormat, PeriodStatPoint } from "../model/types";
import { getPeriodData } from "../model/mockPeriodData";
import { StatisticsPeriodSelector } from "./StatisticsPeriodSelector";
import { StatisticsSpentChart } from "./StatisticsSpentChart";
import { StatisticsConversionsChart } from "./StatisticsConversionsChart";
import { StatisticsTable } from "./StatisticsTable";

export const CampaignStatisticsPage = () => {
  const { id } = useParams<{ id: string }>();

  const [format, setFormat] = useState<PeriodFormat>("week");
  const [offset, setOffset] = useState<number>(0);

  // Определение количества дней для запроса (запрашиваем 90/365 дней, чтобы навигация по периодам была мгновенной)
  const requestedDays = useMemo(() => {
    if (format === "year") return 365;
    return 90;
  }, [format]);

  // Запрос детальной информации и метрик по дням из API
  const { data: campaignDetails, isLoading: isCampaignLoading } =
    useCampaignDetailsQuery(id);
  const {
    data: rawMetrics,
    isLoading: isMetricsLoading,
    error: metricsError,
  } = useCampaignMetricsQuery(id, requestedDays);

  const fallbackCampaign = getCampaignById(id || "1");
  const campaignName = campaignDetails?.name || fallbackCampaign?.name;

  // Трансформация ежедневных метрик бэкенда с учётом выбранного формата и сдвига (offset)
  const periodData = useMemo(() => {
    if (rawMetrics && rawMetrics.length > 0) {
      // Сортируем от более старых к более новым для правильного графика слева направо
      const sorted = [...rawMetrics].sort(
        (a: CampaignDailyMetric, b: CampaignDailyMetric) =>
          new Date(a.report_date).getTime() - new Date(b.report_date).getTime()
      );

      // 1. Формат "Неделя" (7 дней со сдвигом offset)
      if (format === "week") {
        const endIdx = sorted.length + offset * 7;
        const startIdx = endIdx - 7;

        if (endIdx > 0) {
          const actualStart = Math.max(0, startIdx);
          const weekSlice = sorted.slice(actualStart, endIdx);

          if (weekSlice.length >= 2) {
            const points: PeriodStatPoint[] = weekSlice.map(
              (m: CampaignDailyMetric) => {
                const d = new Date(m.report_date);
                const dateStr = d.toLocaleDateString("ru-RU", {
                  day: "2-digit",
                  month: "2-digit",
                });
                const ctr =
                  m.impressions > 0
                    ? Number(((m.clicks / m.impressions) * 100).toFixed(2))
                    : 0;
                const cpa =
                  m.conversions > 0 ? Math.round(m.cost / m.conversions) : 0;

                return {
                  date: dateStr,
                  spent: m.cost,
                  clicks: m.clicks,
                  impressions: m.impressions,
                  ctr,
                  conversions: m.conversions,
                  cpa,
                };
              }
            );

            const firstItem = weekSlice[0];
            const lastItem = weekSlice[weekSlice.length - 1];
            if (firstItem && lastItem) {
              const startD = new Date(firstItem.report_date);
              const endD = new Date(lastItem.report_date);
              const startDay = startD.getDate();
              const endDay = endD.getDate();
              const startMonth = startD.toLocaleDateString("ru-RU", {
                month: "long",
              });
              const endMonth = endD.toLocaleDateString("ru-RU", {
                month: "long",
              });
              const endYear = endD.getFullYear();

              const title =
                startD.getMonth() === endD.getMonth()
                  ? `${startDay} — ${endDay} ${endMonth} ${endYear}`
                  : `${startDay} ${startMonth} — ${endDay} ${endMonth} ${endYear}`;

              return {
                title,
                data: points,
              };
            }
          }
        }
      }

      // 2. Формат "Месяц" (фильтрация по календарному месяцу со сдвигом offset)
      if (format === "month") {
        const now = new Date();
        const targetMonthDate = new Date(
          now.getFullYear(),
          now.getMonth() + offset,
          1
        );
        const monthNameRaw = targetMonthDate.toLocaleDateString("ru-RU", {
          month: "long",
        });
        const monthName =
          monthNameRaw.charAt(0).toUpperCase() + monthNameRaw.slice(1);
        const monthTitle = `${monthName} ${targetMonthDate.getFullYear()}`;

        const monthMetrics = sorted.filter((m) => {
          const d = new Date(m.report_date);
          return (
            d.getMonth() === targetMonthDate.getMonth() &&
            d.getFullYear() === targetMonthDate.getFullYear()
          );
        });

        if (monthMetrics.length >= 2) {
          const points: PeriodStatPoint[] = monthMetrics.map(
            (m: CampaignDailyMetric) => {
              const d = new Date(m.report_date);
              const dateStr = d.toLocaleDateString("ru-RU", {
                day: "2-digit",
                month: "2-digit",
              });
              const ctr =
                m.impressions > 0
                  ? Number(((m.clicks / m.impressions) * 100).toFixed(2))
                  : 0;
              const cpa =
                m.conversions > 0 ? Math.round(m.cost / m.conversions) : 0;

              return {
                date: dateStr,
                spent: m.cost,
                clicks: m.clicks,
                impressions: m.impressions,
                ctr,
                conversions: m.conversions,
                cpa,
              };
            }
          );

          return {
            title: monthTitle,
            data: points,
          };
        }
      }

      // 3. Формат "Год" (агрегация по месяцам со сдвигом offset)
      if (format === "year") {
        const now = new Date();
        const targetYear = now.getFullYear() + offset;
        const yearTitle = `${targetYear} год`;

        const monthAggregates = new Map<
          number,
          {
            spent: number;
            clicks: number;
            impressions: number;
            conversions: number;
          }
        >();

        sorted.forEach((m) => {
          const d = new Date(m.report_date);
          if (d.getFullYear() === targetYear) {
            const mIndex = d.getMonth();
            const cur = monthAggregates.get(mIndex) || {
              spent: 0,
              clicks: 0,
              impressions: 0,
              conversions: 0,
            };
            cur.spent += m.cost;
            cur.clicks += m.clicks;
            cur.impressions += m.impressions;
            cur.conversions += m.conversions;
            monthAggregates.set(mIndex, cur);
          }
        });

        if (monthAggregates.size >= 2) {
          const monthNamesShort = [
            "Янв",
            "Фев",
            "Мар",
            "Апр",
            "Май",
            "Июн",
            "Июл",
            "Авг",
            "Сен",
            "Окт",
            "Ноя",
            "Дек",
          ];
          const points: PeriodStatPoint[] = Array.from(
            monthAggregates.entries()
          )
            .sort(([a], [b]) => a - b)
            .map(([mIndex, stats]) => {
              const ctr =
                stats.impressions > 0
                  ? Number(
                      ((stats.clicks / stats.impressions) * 100).toFixed(2)
                    )
                  : 0;
              const cpa =
                stats.conversions > 0
                  ? Math.round(stats.spent / stats.conversions)
                  : 0;
              return {
                date: monthNamesShort[mIndex] ?? "",
                spent: stats.spent,
                clicks: stats.clicks,
                impressions: stats.impressions,
                ctr,
                conversions: stats.conversions,
                cpa,
              };
            });

          return {
            title: yearTitle,
            data: points,
          };
        }
      }
    }

    // Фоллбэк на моковые данные периода (при сдвиге за пределы реальных дат)
    return getPeriodData(format, offset);
  }, [rawMetrics, format, offset]);

  const isLoading = isCampaignLoading || isMetricsLoading;

  return (
    <div className="flex flex-col gap-5">
      {/* Навигация назад: к кампаниям клиента и к деталям кампании */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-xs">
          <Link
            to={campaignDetails?.client_login ? `/${campaignDetails.client_login}` : "/"}
            className="text-neutral-400 hover:text-neutral-200 transition-colors"
          >
            {campaignDetails?.client_login ? "Кампании клиента" : "Все кампании"}
          </Link>
          <span className="text-neutral-600">/</span>
          <Link
            to={`/campaign/${id || "1"}`}
            className="inline-flex items-center gap-1 font-medium text-neutral-300 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-3.5 h-3.5" />
            <span>Назад к кампании</span>
          </Link>
        </div>
        {campaignName && (
          <span className="text-xs text-neutral-400 font-medium truncate max-w-[280px]">
            {campaignName}
          </span>
        )}
      </div>

      {/* Панель выбора периода и формата */}
      <StatisticsPeriodSelector
        title={periodData.title}
        format={format}
        onFormatChange={(newFormat) => {
          setFormat(newFormat);
          setOffset(0);
        }}
        onPrev={() => setOffset((prev) => prev - 1)}
        onNext={() => setOffset((prev) => Math.min(0, prev + 1))}
        isNextDisabled={offset >= 0}
      />

      {isLoading ? (
        <div className="py-24 flex flex-col items-center justify-center gap-3">
          <Spinner size="lg" />
          <p className="text-xs text-neutral-400">
            Загрузка и синхронизация отчета по дням из Директа...
          </p>
        </div>
      ) : (
        <>
          {metricsError && (
            <div className="p-4 rounded-xl border border-amber-200/50 bg-amber-50/20 text-amber-600 text-xs">
              Не удалось синхронизировать свежие данные отчета. Отображаются
              сохраненные показатели.
            </div>
          )}

          {/* График расхода и кликов */}
          <StatisticsSpentChart data={periodData.data} />

          {/* График конверсий */}
          <StatisticsConversionsChart data={periodData.data} />

          {/* Посуточная таблица показателей */}
          <StatisticsTable data={periodData.data} format={format} />
        </>
      )}
    </div>
  );
};
